import { Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { AuthorizationAwareComponent } from '../../../../core/authorization-aware-component';
import { AppState } from '../../../../app-state';
import { Store as NgrxStore } from '@ngrx/store';
import { BannerComparison } from '../../../../core/banner/banner-comparison';
import * as chroma from 'chroma-js';
import { Chart, ChartOptions, TooltipModel, ChartTypeRegistry, registerables } from 'chart.js';
import { BannerRanking } from '../../../../core/banner/banner-ranking';
import { Store } from '../../../../core/models';
import { buildRankingDescriptor } from '../../store-sales-performance-panel/competitive-analysis-card/comparison-utils';
import { convertToNth } from '../../../../core/util/string-utils';
import { Dictionary } from '../../../../core/dictionary/dictionary';
import { BannerComparisonWizard } from './banner-comparison-wizard/banner-comparison-wizard';
import { WizardRunnerService } from '../../../../core/wizard/wizard-runner.service';
import { KeyIndicatorType } from '../../../../core/user-preferences/key-indicator-type.enum';
import { BannerAnalysisDataService } from '../../../../core/services/banner-analysis-data.service';

Chart.register(...registerables);

@Component({
  standalone: false,
  selector: 'mtn-banner-analysis-card',
  templateUrl: './banner-analysis-card.component.html',
  styleUrls: ['./banner-analysis-card.component.scss']
})
export class BannerAnalysisCardComponent extends AuthorizationAwareComponent implements OnChanges, OnDestroy {

  private readonly GRADIENT = chroma.scale(['red', 'orange', 'limegreen']);

  @ViewChild('chartCanvas')
  chartCanvas: ElementRef<HTMLCanvasElement>;

  @Input()
  comparison: BannerComparison;
  @Input()
  store: Store;

  private chartInstance: Chart | null = null;

  color: string;
  comparisonDescriptor1: string;
  comparisonDescriptor2: string;
  prefix: string;
  ranking: BannerRanking;
  rankingDescriptor: string;
  tooltip: string;

  constructor(protected ngrxStore: NgrxStore<AppState>,
              private wizardRunner: WizardRunnerService,
              private bannerAnalysisDataService: BannerAnalysisDataService,
              private ngZone: NgZone) {
    super(ngrxStore);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.ranking || !changes.ranking?.isFirstChange()) {
      this.ranking = this.comparison.findSubjectBannerRanking();
      this.init();
    }
  }

  ngOnDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
  }

  private renderChart(config: any): void {
    const canvas = this.chartCanvas?.nativeElement;
    if (!canvas) return;

    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }

    // Run Chart.js outside Angular zone to prevent change detection loops
    this.ngZone.runOutsideAngular(() => {
      this.chartInstance = new Chart(canvas, {
        type: 'doughnut',
        data: {
          labels: config.labels,
          datasets: [{
            data: config.data,
            backgroundColor: config.backgroundColor,
            borderColor: config.borderColor,
            hoverBackgroundColor: config.hoverBackgroundColor,
            borderWidth: 2
          }]
        },
        options: config.options
      });
    });
  }

  onAuthorizationChange(): void {
  }

  onAuthorizationInit(): void {
  }

  openComparisonDetailWizard(): void {
    const wizard = new BannerComparisonWizard();
    wizard.model = {
      licenseType: this.licenseType,
      store: this.store,
      comparison: this.comparison,
      userPreferences: this.userPreferences
    };

    this.wizardRunner.run(wizard);
  }

  private buildComparisonDescriptor(): void {
    switch (this.comparison.keyIndicator) {
      case KeyIndicatorType.SALES_SQFT:
        this.comparisonDescriptor1 = Dictionary.labelSalesSqft;
        this.comparisonDescriptor2 = 'Competitor';
        break;
      case KeyIndicatorType.SALES_VOLUME:
        this.comparisonDescriptor1 = Dictionary.labelSalesVolume;
        this.comparisonDescriptor2 = 'Competitor';
        break;
      case KeyIndicatorType.STORE_COUNT:
        this.comparisonDescriptor1 = 'Store Count';
        this.comparisonDescriptor2 = 'Presence';
        break;
    }
  }

  private init(): void {
    if (this.comparison.rankings.length) {
      this.tooltip = this.ranking.getFormattedPercentileString();
      this.rankingDescriptor = buildRankingDescriptor(this.ranking);
      this.buildComparisonDescriptor();

      let verb = 'is';
      if (this.comparison.keyIndicator === KeyIndicatorType.STORE_COUNT) {
        verb = 'has';
      }

      if (this.rankingDescriptor.startsWith('A')) {
        this.prefix = `${this.store.banner.name} ${verb} an`;
      } else {
        this.prefix = `${this.store.banner.name} ${verb} a`;
      }

      this.color = this.GRADIENT(this.ranking.percentile / 100).css();
      const backgroundColor = this.color.replace(')', ',0.6)');
      const hoverBackgroundColor = this.color.replace(')', ',0.5)');

      const chartOptions: ChartOptions = {
        cutout: '75%',
        plugins: {
          legend: { display: false },
          tooltip: {
            bodyAlign: 'center',
            displayColors: false,
            external: (context: { tooltip: TooltipModel<keyof ChartTypeRegistry> }) => {
              const tooltipModel = context.tooltip;
              let index = 1;
              if (tooltipModel.dataPoints && tooltipModel.dataPoints.length) {
                index = tooltipModel.dataPoints[0].dataIndex;
              }
              tooltipModel.opacity = index ? 0 : 1;
            }
          }
        }
      } as ChartOptions;

      const chartConfig = {
        data: [this.ranking.percentile, 100 - this.ranking.percentile],
        labels: [`${convertToNth(this.ranking.percentile)} Percentile`, null],
        backgroundColor: [backgroundColor, 'rgba(0,0,0,0.1)'],
        borderColor: [this.color, 'rgba(0,0,0,0)'],
        hoverBackgroundColor: [hoverBackgroundColor, 'rgba(0,0,0,0.1)'],
        options: chartOptions
      };

      // Defer rendering until after Angular has rendered the *ngIf canvas
      setTimeout(() => this.renderChart(chartConfig), 0);

      const cardData = {
        rank: this.ranking.rank,
        percentile: this.ranking.percentile,
        prefix: this.prefix,
        status: `${this.rankingDescriptor} ${this.comparisonDescriptor2}`,
        description: `by ${this.comparisonDescriptor1}`,
        color: this.color,
        keyIndicator: this.comparison.keyIndicator,
        chartData: chartConfig
      };

      const updateData = {};
      if (this.comparison.keyIndicator === KeyIndicatorType.SALES_SQFT) {
        updateData['salesSqftData'] = cardData;
      } else if (this.comparison.keyIndicator === KeyIndicatorType.SALES_VOLUME) {
        updateData['salesVolumeData'] = cardData;
      } else if (this.comparison.keyIndicator === KeyIndicatorType.STORE_COUNT) {
        updateData['storeCountData'] = cardData;
      }

      this.bannerAnalysisDataService.updateBannerAnalysis(updateData);
    }
  }

}
